import { useState } from "react"
import { useNavigate } from "@tanstack/react-router"
import { AnimatePresence, motion } from "motion/react"
import {
	IconAtom2Outline18 as Atom,
	IconChevronDownOutline18 as ChevronDown,
	IconChevronRightOutline18 as ChevronRight,
	IconCircleWarningOutline18 as CircleWarning,
} from "nucleo-ui-outline-18"

import { AI_BUDGET_REACHED_MESSAGE } from "@productlane/lib"
import { parseThreadActivity } from "@productlane/lib/src/browser"
import { Button } from "@productlane/ui"

import type { TimelineEventProps } from "./types"
import { ActivityEvent } from "./activity-event"
import { SystemEvent } from "./base/system-event"

export function RoutineRunEvent({
	event,
	hasEventAbove = false,
	hasEventBelow = false,
}: TimelineEventProps) {
	const [expanded, setExpanded] = useState(false)
	const navigate = useNavigate()

	if (event.type !== "routineRun") return null

	const { run } = event
	let parsed
	try {
		parsed = parseThreadActivity(
			run.runActivity.threadActivity.eventType,
			run.runActivity.threadActivity.payload,
		)
	} catch {
		return null
	}
	if (parsed.eventType !== "routine_run") return null

	const { status, steps, actions, skipped, error } = parsed.payload
	const currentStep = steps[steps.length - 1]
	const members = run.events
	const Chevron = expanded ? ChevronDown : ChevronRight

	if (status === "running") {
		return (
			<SystemEvent
				icon={<Atom className="h-3.5 w-3.5 flex-none text-secondary" />}
				timestamp={new Date(run.runActivity.threadActivity.createdAt)}
				hasEventAbove={hasEventAbove}
				hasEventBelow={hasEventBelow}
			>
				<span className="flex h-4 min-w-0 items-center text-tertiary">
					<AnimatePresence initial={false} mode="wait">
						<motion.span
							key={currentStep?.label ?? "start"}
							initial={{ opacity: 0, y: 2 }}
							animate={{ opacity: 1, y: 0 }}
							exit={{ opacity: 0, y: -2 }}
							transition={{ duration: 0.16, ease: [0.22, 1, 0.36, 1] }}
							className="ai-loading-shimmer truncate"
						>
							{currentStep?.label ?? "Reading the thread"}
						</motion.span>
					</AnimatePresence>
				</span>
			</SystemEvent>
		)
	}

	if (status === "failed") {
		// A spent budget is the one failure the reader can undo, so it says so in
		// full and carries the way back instead of truncating into a tooltip.
		const budgetReached = error === AI_BUDGET_REACHED_MESSAGE
		return (
			<SystemEvent
				icon={
					<CircleWarning className="h-3.5 w-3.5 flex-none text-secondary" />
				}
				timestamp={new Date(run.runActivity.threadActivity.createdAt)}
				hasEventAbove={hasEventAbove}
				hasEventBelow={hasEventBelow}
			>
				{budgetReached ? (
					<span className="flex min-w-0 flex-wrap items-center gap-1.5 text-tertiary">
						<span>Routine skipped: the AI spend limit is reached.</span>
						<Button
							variant="link"
							size="xs"
							onClick={() => void navigate({ to: "/settings/ai/usage" })}
						>
							Raise the limit
						</Button>
					</span>
				) : (
					<span className="truncate text-tertiary" title={error}>
						{error || "Routine could not finish"}
					</span>
				)}
			</SystemEvent>
		)
	}

	if (actions.length === 0 && members.length === 0) {
		return (
			<>
				<SystemEvent
					icon={<Atom className="h-3.5 w-3.5 flex-none text-secondary" />}
					timestamp={new Date(run.runActivity.threadActivity.createdAt)}
					hasEventAbove={hasEventAbove}
					hasEventBelow={hasEventBelow || skipped.length > 0}
				>
					<span className="truncate text-tertiary">
						Routine found nothing to change
					</span>
				</SystemEvent>
				<SkippedReasons
					reasons={skipped}
					createdAt={run.runActivity.threadActivity.createdAt}
					hasEventBelow={hasEventBelow}
				/>
			</>
		)
	}

	const count = members.length || actions.length
	const label = `Routine made ${count} ${count === 1 ? "change" : "changes"}`

	return (
		<div className={expanded && !hasEventBelow ? "pb-2" : undefined}>
			<SystemEvent
				icon={<Atom className="h-3.5 w-3.5 flex-none text-secondary" />}
				timestamp={new Date(run.runActivity.threadActivity.createdAt)}
				hasEventAbove={hasEventAbove}
				hasEventBelow={hasEventBelow || expanded}
			>
				<Button
					variant="link-no-hover"
					size="sm"
					onClick={() => setExpanded((value) => !value)}
					iconRight={Chevron}
					aria-expanded={expanded}
					className="h-auto min-w-0 max-w-full justify-start gap-1.5 py-0 text-xs font-normal"
					wrapperClassName="gap-1.5"
				>
					<span className="flex-none text-secondary">{label}</span>
					{!expanded ? (
						<span className="truncate text-tertiary">
							{actions.join(" · ")}
						</span>
					) : null}
				</Button>
			</SystemEvent>

			{expanded ? (
				<>
					{members.map((member, index) => (
						<ActivityEvent
							key={member.threadActivity.id}
							event={member}
							hasEventAbove
							hasEventBelow={
								index === members.length - 1
									? hasEventBelow || skipped.length > 0
									: true
							}
						/>
					))}
					<SkippedReasons
						reasons={skipped}
						createdAt={run.runActivity.threadActivity.createdAt}
						hasEventBelow={hasEventBelow}
					/>
				</>
			) : null}
		</div>
	)
}

function SkippedReasons({
	reasons,
	createdAt,
	hasEventBelow,
}: {
	reasons: Array<string>
	createdAt: number
	hasEventBelow: boolean
}) {
	return (
		<>
			{reasons.map((reason, index) => (
				<SystemEvent
					key={reason}
					icon={
						<CircleWarning className="h-3.5 w-3.5 flex-none text-secondary" />
					}
					timestamp={new Date(createdAt)}
					hasEventAbove
					hasEventBelow={index === reasons.length - 1 ? hasEventBelow : true}
				>
					<span className="truncate text-tertiary" title={reason}>
						{reason}
					</span>
				</SystemEvent>
			))}
		</>
	)
}
